import Decimal from 'decimal.js';

/**
 * P11 B5 PR-E – Phân bổ chi phí chuyến nhiều đơn (DEC-262 A, docs/phases/P11.md §10). Hàm thuần dùng chung server + giao diện.
 * - Chia theo kg; hàng cồng kềnh (kg / m³ dưới ngưỡng) chia theo khối quy đổi: cơ sở = max(kg, m³ × ngưỡng kg/m³).
 * - Làm tròn đồng (ROUND_HALF_UP); phần làm tròn dồn vào phiếu có cơ sở lớn nhất → tổng luôn bằng chi phí chuyến.
 * - Không phiếu nào có cơ sở (kg = 0, khối trống) → chia đều.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

/** Mặc định 250 kg/m³: hàng nhẹ hơn mức này (ống nhựa, ống gió, quạt…) tính theo khối quy đổi. Sửa ở Cài đặt điều phối. */
export const DEFAULT_BULKY_KG_PER_M3 = 250;

export const TRIP_COST_METHODS = ['kg', 'volume', 'equal', 'manual'] as const;
export type TripCostMethod = (typeof TRIP_COST_METHODS)[number];
export const TRIP_COST_METHOD_LABELS: Record<TripCostMethod, string> = {
  kg: 'Theo kg',
  volume: 'Theo khối (hàng cồng kềnh)',
  equal: 'Chia đều (không có kg / khối)',
  manual: 'Giám đốc chỉnh tay',
};

export interface TripCostNoteInput {
  noteId: number;
  kg: number;
  /** null = chưa đủ khối tích → tính theo kg */
  volumeM3: number | null;
}

export interface TripCostAllocation {
  noteId: number;
  method: TripCostMethod;
  /** Cơ sở chia: kg hoặc kg quy đổi từ khối */
  basis: number;
  amount: number;
  /** Phần làm tròn đã dồn vào phiếu này (đồng, có thể âm) */
  rounding: number;
}

export function allocationBasis(n: TripCostNoteInput, bulkyKgPerM3: number): { basis: number; method: 'kg' | 'volume' } {
  const kg = Math.max(0, n.kg || 0);
  const volKg = n.volumeM3 !== null && n.volumeM3 > 0 && bulkyKgPerM3 > 0 ? new D(n.volumeM3).times(bulkyKgPerM3).toDecimalPlaces(3).toNumber() : 0;
  return volKg > kg ? { basis: volKg, method: 'volume' } : { basis: new D(kg).toDecimalPlaces(3).toNumber(), method: 'kg' };
}

/** Chia `total` (đồng, số nguyên ≥ 0) cho các phiếu. Thứ tự kết quả = thứ tự đầu vào. */
export function allocateTripCost(total: number, notes: TripCostNoteInput[], bulkyKgPerM3 = DEFAULT_BULKY_KG_PER_M3): TripCostAllocation[] {
  if (!notes.length) return [];
  const t = new D(total).toDecimalPlaces(0);
  const bases = notes.map((n) => allocationBasis(n, bulkyKgPerM3));
  const sum = bases.reduce((s, b) => s.plus(b.basis), new D(0));
  const equal = sum.lte(0);
  const out: TripCostAllocation[] = notes.map((n, i) => {
    const b = bases[i]!;
    const share = equal ? t.div(notes.length) : t.times(b.basis).div(sum);
    return { noteId: n.noteId, method: equal ? 'equal' : b.method, basis: b.basis, amount: share.toDecimalPlaces(0).toNumber(), rounding: 0 };
  });
  const diff = t.minus(out.reduce((s, a) => s + a.amount, 0)).toNumber();
  if (diff !== 0) {
    let big = 0;
    out.forEach((a, i) => {
      if (a.basis > out[big]!.basis) big = i;
    });
    out[big]!.amount += diff;
    out[big]!.rounding = diff;
  }
  return out;
}

/** Kiểm phân bổ tay: đủ phiếu, không âm, số nguyên, tổng = chi phí chuyến. Trả danh sách lỗi (rỗng = hợp lệ). */
export function checkManualAllocation(total: number, noteIds: number[], amounts: { noteId: number; amount: number }[]): string[] {
  const errors: string[] = [];
  const seen = new Set<number>();
  for (const a of amounts) {
    if (!noteIds.includes(a.noteId)) errors.push(`Phiếu #${a.noteId} không thuộc chuyến`);
    if (seen.has(a.noteId)) errors.push(`Phiếu #${a.noteId} nhập 2 lần`);
    seen.add(a.noteId);
    if (!Number.isInteger(a.amount) || a.amount < 0) errors.push(`Số tiền phiếu #${a.noteId} phải là số nguyên ≥ 0`);
  }
  for (const id of noteIds) if (!seen.has(id)) errors.push(`Thiếu số tiền cho phiếu #${id}`);
  const s = amounts.reduce((x, a) => x + a.amount, 0);
  if (!errors.length && s !== total) errors.push(`Tổng phân bổ ${s.toLocaleString('vi-VN')} đ khác chi phí chuyến ${total.toLocaleString('vi-VN')} đ`);
  return errors;
}

/**
 * Km GPS của xe trong ngày chia cho các "lượt": mỗi chuyến = 1 lượt, mỗi phiếu đã giao không thuộc chuyến = 1 lượt (như B4).
 * Khóa lượt dạng chuỗi ('t:12', 'n:34'); làm tròn 1 số lẻ, lượt cuối nhận phần dư.
 */
export function splitDayKmUnits(gpsKm: number, units: string[]): Map<string, number> {
  const out = new Map<string, number>();
  if (!units.length) return out;
  const each = new D(gpsKm).div(units.length).toDecimalPlaces(1);
  let used = new D(0);
  units.forEach((u, i) => {
    const v = i === units.length - 1 ? new D(gpsKm).minus(used).toDecimalPlaces(1) : each;
    used = used.plus(v);
    out.set(u, v.toNumber());
  });
  return out;
}
